import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Close } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: { fontSize: '23px', fontWeight: 550, color: theme.color.grey.neutral400, paddingLeft: '5px' },
    subtitle: { fontSize: '14px', paddingLeft: '5px', paddingRight: '10px' },
    content: { padding: '12px 24px 16px 24px' },
    field: { marginBottom: '16px' },
  })
);

interface Props {
  open: boolean;
  volunteerName: string;
  subtitle?: string;
  templates: { certificateTemplateId: string; name: string }[];
  saving: boolean;
  onClose: () => void;
  onIssue: (certificateTemplateId: string, awardedFor: string) => void;
}

export const IssueCertificateDialog = ({
  open,
  volunteerName,
  subtitle,
  templates,
  saving,
  onClose,
  onIssue,
}: Props) => {
  const classes = useStyles();
  const [templateId, setTemplateId] = useState('');
  const [awardedFor, setAwardedFor] = useState('');

  useEffect(() => {
    if (open) {
      setTemplateId(templates.length === 1 ? templates[0].certificateTemplateId : '');
      setAwardedFor('');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography className={classes.title}>Issue Certificate</Typography>
          <IconButton aria-label='Close' color='inherit' onClick={onClose}>
            <Close color='action' />
          </IconButton>
        </Box>
        <Box pr={3} mt={1}>
          <Typography className={classes.subtitle} color='textSecondary'>
            {subtitle ?? `Issue a certificate to ${volunteerName}. No email is sent.`}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {templates.length === 0 ? (
          <Typography style={{ fontSize: '14px', color: '#808080' }}>
            There are no certificate templates yet. Add one in the admin console under Recognition.
          </Typography>
        ) : (
          <>
            <FormControl fullWidth variant='outlined' className={classes.field}>
              <InputLabel>Template</InputLabel>
              <Select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value as string)}
                input={<OutlinedInput label='Template' />}
              >
                {templates.map((template) => (
                  <MenuItem key={template.certificateTemplateId} value={template.certificateTemplateId}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              variant='outlined'
              label='Awarded for (optional)'
              value={awardedFor}
              onChange={(e) => setAwardedFor(e.target.value)}
              placeholder='Completing Food Safety Level 2'
              helperText='Printed wherever the template uses {{AWARDED_FOR}}. Leave blank if your template does not use it.'
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <TabletButton variant='text' color='error' onClick={onClose} disabled={saving} style={{ marginRight: 8 }}>
          Cancel
        </TabletButton>
        <TabletButton
          variant='contained'
          color='primary'
          disabled={saving || !templateId}
          onClick={() => onIssue(templateId, awardedFor)}
        >
          Issue
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
};
